import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";

export default function AdminWhatsApp() {
  const [cfg, setCfg] = useState(null);
  const [sids, setSids] = useState({});
  const [rows, setRows] = useState([]);
  const [to, setTo] = useState("");
  const [tpl, setTpl] = useState("smartin_order_status");
  const [busy, setBusy] = useState(false);
  const loadLog = () => adminApi.get("/whatsapp/log").then((r) => setRows(r.data.rows)).catch(() => {});
  useEffect(() => {
    adminApi.get("/whatsapp").then((r) => { setCfg(r.data); setSids(Object.fromEntries(r.data.templates.map((t) => [t.name, t.sid]))); }).catch((e) => toast.error(errMsg(e)));
    loadLog();
  }, []);
  const save = async () => {
    try { const { data } = await adminApi.put("/whatsapp", { sids }); setCfg(data); toast.success("Template SIDs saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await adminApi.post("/whatsapp/test", { to, template: tpl }); toast.success(`Sent: ${data.status}${data.twilio_sid ? ` (${data.twilio_sid})` : ""}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); loadLog(); }
  };
  if (!cfg) return <p className="text-sm text-slate-400">Loading…</p>;
  const field = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900";
  return (
    <div className="space-y-6" data-testid="admin-whatsapp">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">WhatsApp</h1>
        <p className="text-sm text-slate-400" data-testid="whatsapp-configured">{cfg.configured ? `Twilio connected · sender ${cfg.from}` : "Twilio is not configured. Messages are logged but not sent."}</p>
      </div>
      <section className="space-y-2">
        <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-500">Templates · blank SID sends plain text</h2>
        {cfg.templates.map((t) => (
          <div key={t.name} className="grid gap-2 rounded-xl border border-slate-200 bg-white p-3 lg:grid-cols-[220px_1fr_300px] lg:items-center" data-testid={`whatsapp-template-${t.name}`}>
            <div><div className="font-mono text-sm font-bold text-slate-900">{t.name}</div><div className="text-xs text-slate-400">{t.category}</div></div>
            <div className="text-xs text-slate-600">{t.body}</div>
            <input value={sids[t.name] || ""} onChange={(e) => setSids((s) => ({ ...s, [t.name]: e.target.value }))} placeholder="HX… Content SID" className={`${field} font-mono`} data-testid={`whatsapp-sid-${t.name}`} />
          </div>
        ))}
        <button onClick={save} className="h-10 rounded-lg bg-amber-500 px-5 text-sm font-bold text-slate-950" data-testid="whatsapp-save-sids">Save SIDs</button>
      </section>
      <section className="flex flex-wrap items-end gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+923001234567" className={field} data-testid="whatsapp-test-to" />
        <select value={tpl} onChange={(e) => setTpl(e.target.value)} className={field} data-testid="whatsapp-test-template">{cfg.templates.map((t) => <option key={t.name}>{t.name}</option>)}</select>
        <button onClick={test} disabled={!to.trim() || busy} className="flex h-10 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-bold text-white disabled:opacity-50" data-testid="whatsapp-test-send"><Send className="h-4 w-4" />Send test message</button>
      </section>
      <section>
        <h2 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-500">Send log · latest 200</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left text-xs text-slate-600" data-testid="whatsapp-log">
            <thead className="bg-slate-100 text-slate-400"><tr>{["When", "Type", "To", "Status", "Mode", "Twilio SID", "Error"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"><td className="px-3 py-2">{new Date(r.created_at).toLocaleString()}</td><td className="px-3 py-2 font-mono">{r.kind}</td><td className="px-3 py-2 font-mono">{r.to_masked}</td>
                <td className={`px-3 py-2 font-bold ${r.status === "failed" ? "text-rose-600" : "text-emerald-700"}`}>{r.status}</td><td className="px-3 py-2">{r.mode || "—"}</td><td className="px-3 py-2 font-mono">{r.twilio_sid || "—"}</td><td className="max-w-xs px-3 py-2 text-rose-600">{r.error || ""}</td></tr>
            ))}</tbody>
          </table>
          {!rows.length && <p className="p-4 text-sm text-slate-500">No messages yet.</p>}
        </div>
      </section>
    </div>
  );
}
